import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useSelectedTimerStore } from './selectedTimerStore';
import { useThemeStore } from './themeStore';

export type SubAlarm = {
    id: string;
    /** Human-readable label shown when the sub-alarm fires. */
    label: string;
    /** Emoji icon for the sub-alarm (shower 🚿, brush 🪥, etc.). */
    emoji: string;
    /** Minutes elapsed (from timer start) at which this alarm fires. */
    atElapsedMin: number;
    /**
     * Sound source. If it starts with `kid:` (e.g. `kid:fanfare`), it's a
     * procedural kid-sound from utils/kidSounds.ts. Otherwise it's a URL
     * (e.g. from myinstants.com). Empty = default alarm.
     */
    soundSrc: string;
    /** Optional phase color override (hex). Empty = theme point color. */
    pointColor?: string;
};

export type RecurringWarning = {
    /** Interval in minutes at which the warning fires (e.g. 1 = every minute). */
    intervalMin: number;
    /** Sound to play (kid: prefix or URL). */
    soundSrc: string;
    /** Label shown in the toast. */
    label: string;
    /** Emoji for the toast. */
    emoji: string;
};

export type Profile = {
    id: string;
    name: string;
    /** Theme id applied when this profile is active. */
    themeId: string;
    /** Total timer duration in minutes. */
    totalMinutes: number;
    /** Sub-alarms that fire at elapsed-minute thresholds. */
    subAlarms: SubAlarm[];
    /** Optional recurring warning that fires every N minutes (e.g. Exam mode). */
    recurringWarning?: RecurringWarning;
};

type ProfileState = {
    profiles: Profile[];
    activeProfileId: string | null;
    addProfile: (profile: Profile) => void;
    updateProfile: (id: string, updatedProps: Partial<Profile>) => void;
    removeProfile: (id: string) => void;
    setActiveProfile: (id: string | null) => void;
    getActiveProfile: () => Profile | undefined;
    updateSubAlarm: (profileId: string, subAlarmId: string, updatedProps: Partial<SubAlarm>) => void;
    autoAssignPhaseColors: () => void;
};

const MORNING_ROUTINE: Profile = {
    id: 'profile-morning-routine',
    name: 'Morning Routine',
    themeId: 'anime-dragonball-z',
    totalMinutes: 15,
    subAlarms: [
        { id: 'shower', label: 'Shower time!', emoji: '🚿', atElapsedMin: 7, soundSrc: 'kid:sparkle' },
        { id: 'brush', label: 'Brush teeth!', emoji: '🪥', atElapsedMin: 11, soundSrc: 'kid:ding' },
        { id: 'dressed', label: 'Get dressed!', emoji: '👕', atElapsedMin: 15, soundSrc: 'kid:fanfare' },
    ],
};

const EXAM: Profile = {
    id: 'profile-exam',
    name: 'Exam',
    themeId: 'anime-conan',
    totalMinutes: 30,
    subAlarms: [{ id: 'timeup', label: 'Time is up!', emoji: '⏰', atElapsedMin: 30, soundSrc: 'kid:bell' }],
    recurringWarning: {
        intervalMin: 1,
        soundSrc: 'kid:lowbeep',
        label: '1 minute passed',
        emoji: '📝',
    },
};

const DEFAULT_PROFILES: Profile[] = [MORNING_ROUTINE, EXAM];

export const useProfileStore = create<ProfileState>()(
    persist(
        (set, get) => ({
            profiles: DEFAULT_PROFILES,
            activeProfileId: null, // Default: plain timer, no profile

            addProfile: (profile) => set((state) => ({ profiles: [...state.profiles, profile] })),

            updateProfile: (id, updatedProps) =>
                set((state) => ({
                    profiles: state.profiles.map((p) => (p.id === id ? { ...p, ...updatedProps } : p)),
                })),

            removeProfile: (id) =>
                set((state) => {
                    if (id === MORNING_ROUTINE.id || id === EXAM.id) {
                        return {};
                    }
                    const updated = state.profiles.filter((p) => p.id !== id);
                    const newActive =
                        state.activeProfileId === id ? null : state.activeProfileId;
                    return { profiles: updated, activeProfileId: newActive };
                }),

            setActiveProfile: (id) => {
                const profile = id ? get().profiles.find((p) => p.id === id) : undefined;

                if (profile) {
                    set({ activeProfileId: id });
                    useThemeStore.getState().setTheme(profile.themeId);
                    useSelectedTimerStore.getState().updateDefaultTimer({
                        time: profile.totalMinutes,
                        isMinutes: true,
                    });
                } else {
                    set({ activeProfileId: null });
                    // Keep current theme, reset to 10 min plain mode
                    useSelectedTimerStore.getState().updateDefaultTimer({
                        time: 10,
                        isMinutes: true,
                    });
                }
            },

            getActiveProfile: () => {
                const id = get().activeProfileId;
                return id ? get().profiles.find((p) => p.id === id) : undefined;
            },

            autoAssignPhaseColors: () =>
                set((state) => {
                    const id = state.activeProfileId;
                    if (!id) return {};
                    const profile = state.profiles.find((p) => p.id === id);
                    if (!profile) return {};
                    const theme = useThemeStore.getState().selectedTheme;
                    const options = theme.color.pointOptions || [theme.color.point];
                    const updatedSubs = profile.subAlarms.map((s, i) => ({
                        ...s,
                        pointColor: options[i % options.length],
                    }));
                    return {
                        profiles: state.profiles.map((p) =>
                            p.id === id ? { ...p, subAlarms: updatedSubs } : p
                        ),
                    };
                }),

            updateSubAlarm: (profileId, subAlarmId, updatedProps) =>
                set((state) => ({
                    profiles: state.profiles.map((p) =>
                        p.id === profileId
                            ? {
                                  ...p,
                                  subAlarms: p.subAlarms.map((s) =>
                                      s.id === subAlarmId ? { ...s, ...updatedProps } : s
                                  ),
                              }
                            : p
                    ),
                })),
        }),
        {
            name: 'profile-store',
            version: 4,
            migrate: (persistedState, version) => {
                const state = persistedState as ProfileState;
                if (version < 4 || !state.profiles || state.profiles.length === 0) {
                    // v4 defaults to null (no profile selected)
                    let profiles = state.profiles && state.profiles.length > 0 ? state.profiles : DEFAULT_PROFILES;
                    if (!profiles.some((p) => p.id === EXAM.id)) {
                        profiles = [...profiles, EXAM];
                    }
                    return {
                        ...state,
                        profiles,
                        activeProfileId: null,
                    };
                }
                return state;
            },
        }
    )
);
